import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {usePostTiresRestoreMutation} from '@dms/api/core';

import type {QueryFilterObject, ActionCallback} from 'features/eag-grid';

import {catchUnhandledDataGridActions} from '../../../utils/catchUnhandledDataGridActions';
import {handleApiError} from '../../../utils/handleApiError';
import {DmsDataGrid} from '../../DmsDataGrid/DmsDataGrid';
import {Wheel} from '../types/Wheel';

interface ArchivedTiresProps {
  tireSetId: string;
}

const PREDEFINED_COLUMNS = ['id', 'orderId'] as const;

export function ArchivedTires(props: ArchivedTiresProps) {
  const [postTiresRestore] = usePostTiresRestoreMutation();

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {orderSetId: props.tireSetId}]),
    [props.tireSetId]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = (data) => {
    const {actionKey, rowData, getColumnData, refreshData} = data;

    const wheelsArray = isArray(rowData) ? (rowData as Wheel[]) : [rowData as Wheel];

    const wheels = wheelsArray
      .map((wheel) => wheel.id)
      .filter((id): id is string => id != null)
      .map((id) => ({id}));

    const tireOrderId = getColumnData<string>('orderId')?.value;

    if (!tireOrderId || !wheels.length) {
      return;
    }

    match(actionKey)
      .with('restoreWheel', () => {
        postTiresRestore({
          orderId: tireOrderId,
          setId: props.tireSetId,
          body: {wheels},
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <DmsDataGrid
      autoHeight
      gridCode="tire-set-archived-wheel"
      queryModifier={queryModifier}
      predefinedColumns={PREDEFINED_COLUMNS}
      actionCallback={actionCallback}
    />
  );
}
